import { Box, Button, FormControl, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import TextEditorQuill from "../../components/TextEditorQuill";
import { useApi } from "../../hooks/useApi.hook";
import { IRules } from "../../models/office";
import OfficeService from "../../services/api/office.service";
import { toggleMessage } from "../../components/Toast/Toast";
import { IFeaturePermission } from "../../models/user/models.roles";
import { authTokens } from "../../services/services";
import RoleService from "../../services/api/role.service";
import { ITokenPayload } from "../../models/user";
import {
  FeatureModel,
  RulesPermissionModel
} from "../../models/common/models.enum";
import "react-quill/dist/quill.core.css";
import EMPTY from "../../assets/images/common/rule_empty_data.svg";
import { ImageCustom } from "../../components";
import { useTranslation } from "react-i18next";

type Props = {};

const Rules = (props: Props) => {
  const { t } = useTranslation();

  const [isEdit, setIsEdit] = useState(false);
  const [rules, setRules] = useState<string | undefined>();
  const [userInfo, setUserInfo] = useState<ITokenPayload | null>(null);

  const getRules = useApi<IRules>({});
  const updateRules = useApi<IRules>({});
  const features = useApi<IFeaturePermission[]>({ isFetch: true });

  function fetchData() {
    getRules.request(OfficeService.get());
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    async function getUserInfo() {
      const user = await authTokens.getUser();
      user && setUserInfo(user);
    }
    getUserInfo();
  }, []);

  useEffect(() => {
    if (userInfo)
      features.request(
        RoleService.getFeaturePemissionByUserId(userInfo.userId)
      );
  }, [userInfo]);

  const canUpdate = !!features.data?.find(
    f =>
      f.feature === FeatureModel.Rules &&
      f.permissions.includes(RulesPermissionModel.Update)
  );

  const hasDescription = !!getRules.data?.description;

  return (
    <Box
      sx={{
        padding: "12px",
        justifyItems: "flex-end"
      }}
    >
      <Box height={"44px"}>
        {canUpdate && !isEdit && (
          <Button
            variant="contained"
            onClick={() => {
              setRules(getRules.data?.description);
              setIsEdit(true);
            }}
          >
            {t("Rules.Buttons.EditRules")}
          </Button>
        )}
      </Box>
      <Box
        sx={{
          backgroundColor: "white",
          margin: "16px auto",
          width: "50%",
          minHeight: "100vh",
          padding: "64px"
        }}
      >
        {!isEdit && (
          <div
            className="view ql-editor"
            dangerouslySetInnerHTML={{
              __html: hasDescription ? getRules.data!.description : ""
            }}
          ></div>
        )}

        {!isEdit &&
          hasDescription &&
          getRules.data!.description.split("<").length === 4 &&
          getRules.data!.description.split("<")[2] === "br>" && (
            <Box justifyItems={"center"} margin="20% 0">
              <ImageCustom src={EMPTY} width="auto" height="300px" />
              <Typography variant="subtitle2" color={"#596970"}>
                {t("Rules.Empty.NoRules")}
              </Typography>
            </Box>
          )}

        {!canUpdate && !hasDescription ? (
          <Typography>{t("Rules.Empty.NoData")}</Typography>
        ) : (
          (isEdit || !hasDescription) && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "end"
              }}
            >
              <FormControl fullWidth>
                <TextEditorQuill
                  placeholder={t("Rules.Placeholder.EnterRules")}
                  toolbar={true}
                  editorStyle={{
                    border: "1px solid #D3DCDF",
                    borderRadius: "8px",
                    width: "100%"
                  }}
                  onChange={e => setRules(e)}
                  value={rules ?? ""}
                />
              </FormControl>

              <Box marginTop="12px" display="flex">
                {hasDescription && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    sx={{ borderRadius: "8px", marginRight: "12px" }}
                    onClick={() => setIsEdit(false)}
                    disabled={updateRules.loading}
                  >
                    {t("Rules.Buttons.CancelChanges")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  sx={{ borderRadius: "8px" }}
                  disabled={updateRules.loading}
                  color="success"
                  onClick={() =>
                    rules &&
                    updateRules.request(
                      OfficeService.update({
                        description: rules
                      }).then(res => {
                        if (res.success) {
                          toggleMessage({
                            type: "success",
                            message:
                              res?.message ?? t("Rules.Messages.UpdateSuccess")
                          });
                          setIsEdit(false);
                          fetchData();
                        } else {
                          toggleMessage({
                            type: "error",
                            message:
                              res?.error ?? t("Rules.Messages.UpdateError")
                          });
                        }
                      })
                    )
                  }
                >
                  {t("Rules.Buttons.UpdateRules")}
                </Button>
              </Box>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default Rules;
